import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { User } from "../../../../../api/accessControl/User.ts";
import { BackendApi } from "../../../../../api/api.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import Popup from "../../../../../components/general/Popup.tsx";
import { SecondaryButton } from "../../../../../components/general/SecondaryButton.tsx";
import { useNotifications } from "../../../../../lib/NotificationContext.tsx";
import { downloadBlob } from "../../../../../utils/base64UrlConverters.ts";
import {
    downloadSalesInvoiceDraftPdf,
    downloadSalesInvoicePdf,
} from "../CreateInvoicePage/salesInvoiceApi.ts";
import { SalesInvoiceStatusPill } from "../../components/salesInvoices/SalesInvoiceStatusPill.tsx";
import { InvoiceTimeline } from "../../components/salesInvoices/InvoiceTimeline.tsx";
import {
    SendInvoiceEmailModal,
    type SendInvoiceEmailKind,
    type SendInvoiceEmailValues,
} from "../../components/salesInvoices/SendInvoiceEmailModal.tsx";
import {
    formatDateDDMMYYYY,
    STATUS_META,
} from "../../components/salesInvoices/utils/salesInvoicesFormat.ts";
import { backendErrorDescription } from "../../components/salesInvoices/utils/backendErrorDescription.ts";
import { useCustomers } from "../../../../../hooks/useCustomers.ts";
import {
    buildCreditNoteEmailIntro,
    buildDefaultCreditNoteEmailBody,
    buildDefaultInvoiceEmailBody,
    buildDefaultInvoiceReminderBody,
    buildInvoiceEmailIntro,
    buildInvoiceReminderIntro,
    type InvoiceEmailMessageContext,
} from "../../components/salesInvoices/utils/invoiceEmailMessage.ts";
import type {
    SalesInvoiceDetail,
    SalesInvoiceTimelineEntry,
} from "../../components/salesInvoices/types/salesInvoice.ts";
import { InvoiceActionsCard } from "./components/InvoiceActionsCard.tsx";
import { InvoiceDocumentCard } from "./components/InvoiceDocumentCard.tsx";
import { LinkedCreditNotesCard } from "./components/LinkedCreditNotesCard.tsx";
import { CreditNoteBanner } from "../../components/salesInvoices/CreditNoteBanner.tsx";
import type {SalesInvoiceDisplayStatus} from "../../../../../generated/enums.ts";

const SENT_TIMELINE_TYPES = ["sent", "resent", "reminder_sent", "delivered", "opened", "bounced"];

const DEFAULT_EMAIL_MESSAGE_SETTING = "SALES_INVOICE_DEFAULT_EMAIL_MESSAGE";
const DEFAULT_CREDIT_NOTE_EMAIL_MESSAGE_SETTING = "CREDIT_NOTE_DEFAULT_EMAIL_MESSAGE";
const INVOICE_CONTACT_EMAIL_SETTING = "SALES_INVOICE_CONTACT_EMAIL";

function toEuros(preciseCents: string | number): number {
    const parsed = Number(preciseCents);
    return Number.isFinite(parsed) ? Math.round(parsed) / 100 : 0;
}

function toDisplayStatus(value: string): SalesInvoiceDisplayStatus {
    return value in STATUS_META ? (value as SalesInvoiceDisplayStatus) : "outstanding";
}

function ReminderSettingsCard({
    autoReminderEnabled,
    reminderIntervalDays,
    isSaving,
    onChange,
}: {
    autoReminderEnabled: boolean;
    reminderIntervalDays: number;
    isSaving: boolean;
    onChange: (enabled: boolean, intervalDays: number) => void;
}) {
    return (
        <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Herinneringen</h3>
            <div className="flex items-center justify-between gap-4">
                <label htmlFor="auto-reminder-toggle" className="text-sm text-gray-700">
                    Automatische herinneringen tot betaald (elke {reminderIntervalDays} dagen na
                    vervaldatum)
                </label>
                <input
                    id="auto-reminder-toggle"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    checked={autoReminderEnabled}
                    disabled={isSaving}
                    onChange={(event) => onChange(event.target.checked, reminderIntervalDays)}
                />
            </div>
        </div>
    );
}

export default function SalesInvoiceDetailPage({ currentUser }: { currentUser: User }) {
    const { invoiceId } = useParams<{ invoiceId: string }>();
    const navigate = useNavigate();
    const api = new BackendApi(currentUser.getToken());
    const { dispatch } = useNotifications();
    const { customers, changeCustomer } = useCustomers(currentUser);

    const [invoice, setInvoice] = useState<SalesInvoiceDetail | null>(null);
    const [timeline, setTimeline] = useState<SalesInvoiceTimelineEntry[] | null>(null);
    const [timelineFailed, setTimelineFailed] = useState(false);
    const [logoDataUri, setLogoDataUri] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [emailModalKind, setEmailModalKind] = useState<SendInvoiceEmailKind | null>(null);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [isDiscardDraftOpen, setIsDiscardDraftOpen] = useState(false);
    const [isDiscardingDraft, setIsDiscardingDraft] = useState(false);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const [isSavingReminderSettings, setIsSavingReminderSettings] = useState(false);
    const [standardEmailText, setStandardEmailText] = useState("");
    const [standardCreditNoteText, setStandardCreditNoteText] = useState("");
    const [invoiceContactEmail, setInvoiceContactEmail] = useState(currentUser.email);

    const loadSequence = useRef(0);

    async function load(options: { showSpinner: boolean } = { showSpinner: true }) {
        const sequence = ++loadSequence.current;
        const isCurrent = () => sequence === loadSequence.current;
        if (!invoiceId) {
            setLoadError("De factuur kon niet worden gevonden.");
            setIsLoading(false);
            return;
        }
        if (options.showSpinner) setIsLoading(true);
        try {
            const [invoiceData, timelineData] = await Promise.all([
                api.Me__business__getSalesInvoice(invoiceId),
                api.Me__business__getSalesInvoiceTimeline(invoiceId).then(
                    (data) => ({
                        entries: data.entries as SalesInvoiceTimelineEntry[],
                        failed: false,
                    }),
                    () => ({ entries: null, failed: true })
                ),
            ]);
            if (!isCurrent()) return;
            setInvoice(invoiceData as SalesInvoiceDetail);
            setTimeline(timelineData.entries);
            setTimelineFailed(timelineData.failed);
            setLoadError(null);
        } catch (e) {
            if (!isCurrent()) return;
            console.error(e);
            setLoadError("De factuur kon niet worden geladen.");
        } finally {
            if (isCurrent()) setIsLoading(false);
        }
    }

    async function loadLogo() {
        try {
            const data = await api.Me__business__logo({});
            setLogoDataUri(`data:${data.content_type};base64,${data.file_data}`);
        } catch {
            setLogoDataUri(null);
        }
    }

    useEffect(() => {
        load();
        loadLogo();
        loadDefaultEmailMessage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, invoiceId]);

    async function loadDefaultEmailMessage() {
        try {
            await currentUser.businessSettings.refresh();
            const stored = currentUser.businessSettings.getSetting(DEFAULT_EMAIL_MESSAGE_SETTING);
            setStandardEmailText(typeof stored === "string" ? stored : "");
            const storedCreditNote = currentUser.businessSettings.getSetting(
                DEFAULT_CREDIT_NOTE_EMAIL_MESSAGE_SETTING,
            );
            setStandardCreditNoteText(typeof storedCreditNote === "string" ? storedCreditNote : "");
        } catch {
            setStandardEmailText("");
            setStandardCreditNoteText("");
        }

        try {
            const storedContact = currentUser.businessSettings.getSetting(INVOICE_CONTACT_EMAIL_SETTING);
            setInvoiceContactEmail(String(storedContact ?? "").trim() || currentUser.email);
        } catch {
            setInvoiceContactEmail(currentUser.email);
        }
    }

    const isReminderEmail = emailModalKind === "reminder";

    const isCreditNoteEmail = invoice?.is_credit_note ?? false;

    const introForModal = !invoice
        ? ""
        : isCreditNoteEmail
            ? buildCreditNoteEmailIntro(emailMessageContext(invoice))
            : isReminderEmail
                ? buildInvoiceReminderIntro(emailMessageContext(invoice))
                : buildInvoiceEmailIntro(emailMessageContext(invoice));

    const defaultBodyForModal = !invoice
        ? ""
        : isCreditNoteEmail
            ? standardCreditNoteText.trim() || buildDefaultCreditNoteEmailBody(emailMessageContext(invoice))
            : standardEmailText.trim() || (isReminderEmail
                ? buildDefaultInvoiceReminderBody(emailMessageContext(invoice))
                : buildDefaultInvoiceEmailBody(emailMessageContext(invoice)));

    function emailMessageContext(detail: SalesInvoiceDetail): InvoiceEmailMessageContext {
        return {
            invoiceNumber: detail.invoice_number,
            totalAmount: toEuros(detail.total_payable_cents),
            paymentTermDays: detail.payment_term,
            customerName: detail.customer_name,
            entrepreneurName: detail.seller_contact_name,
            companyName: detail.seller_business_name,
        };
    }

    async function persistSendPreferences(values: SendInvoiceEmailValues) {
        try {
            if (values.rememberMessage) {
                const setting = isCreditNoteEmail
                    ? DEFAULT_CREDIT_NOTE_EMAIL_MESSAGE_SETTING
                    : DEFAULT_EMAIL_MESSAGE_SETTING;
                await currentUser.businessSettings.setSetting(setting, values.messageBody);
                if (isCreditNoteEmail) {
                    setStandardCreditNoteText(values.messageBody);
                } else {
                    setStandardEmailText(values.messageBody);
                }
            }

            if (values.rememberCc && invoice?.customer_id) {
                const saved = customers.find((candidate) => candidate.id === invoice.customer_id);
                if (saved) {
                    await changeCustomer(saved.id, {...saved, fixed_cc_email: values.ccEmail || null});
                }
            }
        } catch (e) {
            console.error(e);
            dispatch({
                title: "Voorkeuren niet bewaard",
                description: "De factuur is verstuurd, maar je voorkeuren konden niet worden bewaard.",
                type: "error",
            });
        }
    }

    const displayStatus = invoice ? toDisplayStatus(invoice.display_status) : null;
    const isDraft = displayStatus === "draft";
    const hasBeenSent = timelineFailed
        ? invoice?.invoice_number != null
        : timeline?.some((entry) => SENT_TIMELINE_TYPES.includes(entry.type)) ?? false;
    const isUnpaid = displayStatus === "outstanding" || displayStatus === "overdue";

    const displayTimeline: SalesInvoiceTimelineEntry[] | null = !timeline
        ? null
        : !isDraft
            ? timeline
            : [
                ...timeline.map((entry) =>
                    entry.type === "created"
                        ? { ...entry, description: "Concept aangemaakt" }
                        : entry
                ),
                {
                    type: "awaiting_send",
                    occurred_at: "",
                    description: "Wacht op versturen",
                    recipient: null,
                    cc_email: null,
                    failure_reason: null,
                    message_id: null,
                    subtitle: "Nog geen factuurnummer",
                },
            ];

    async function handleSendEmail(values: SendInvoiceEmailValues) {
        if (!invoice || !emailModalKind) return;
        setIsSendingEmail(true);

        if (isDraft) {
            try {
                await api.Me__business__issueSalesInvoice({
                    invoice_id: invoice.id,
                    number_prefix: null,
                });
            } catch (e) {
                console.error(e);
                setIsSendingEmail(false);
                dispatch({
                    title: "Definitief maken mislukt",
                    description: backendErrorDescription(
                        e,
                        "De factuur kon niet definitief worden gemaakt. Probeer het later opnieuw."
                    ),
                    type: "error",
                });
                return;
            }
        }

        const requestBody = {
            invoice_id: invoice.id,
            to_email: values.toEmail,
            cc_email: values.ccEmail,
            bcc_email: values.bccEmail || null,
            reply_to_email: values.replyToEmail || null,
            message: values.message || null,
        };
        try {
            if (emailModalKind === "reminder") {
                await api.Me__business__sendSalesInvoiceReminder(requestBody);
            } else if (emailModalKind === "resend") {
                await api.Me__business__resendSalesInvoice(requestBody);
            } else {
                await api.Me__business__sendSalesInvoice(requestBody);
            }
            dispatch({
                title: emailModalKind === "reminder" ? "Herinnering verstuurd" : "Factuur verstuurd",
                description: `De e-mail is verstuurd naar ${values.toEmail}.`,
                type: "success",
            });
            await persistSendPreferences(values);
            setEmailModalKind(null);
            await load({ showSpinner: false });
        } catch (e) {
            console.error(e);
            dispatch({
                title: isDraft ? "Factuur definitief gemaakt, e-mail niet verstuurd" : "Versturen mislukt",
                description: backendErrorDescription(e),
                type: "error",
            });
            if (isDraft) {
                setEmailModalKind(null);
                await load({ showSpinner: false });
            }
        } finally {
            setIsSendingEmail(false);
        }
    }

    async function handleDiscardDraft() {
        if (!invoice || isDiscardingDraft) return;
        setIsDiscardingDraft(true);
        try {
            await api.Me__business__discardSalesInvoiceDraft({ invoice_id: invoice.id });
            dispatch({
                title: "Concept verwijderd",
                description: "De conceptfactuur is verwijderd.",
                type: "success",
            });
            setIsDiscardDraftOpen(false);
            navigate("/invoices/drafts");
        } catch (e) {
            console.error(e);
            dispatch({
                title: "Fout opgetreden",
                description: backendErrorDescription(
                    e,
                    "Het concept kon niet worden verwijderd. Probeer het later opnieuw."
                ),
                type: "error",
            });
        } finally {
            setIsDiscardingDraft(false);
        }
    }

    async function handleDownloadPdf() {
        if (!invoice) return;
        setIsDownloadingPdf(true);
        try {
            const file = isDraft
                ? await downloadSalesInvoiceDraftPdf(api, invoice.id)
                : await downloadSalesInvoicePdf(api, invoice.id);
            downloadBlob(file.blob, file.fileName);
        } catch (e) {
            console.error(e);
            dispatch({
                title: "Download mislukt",
                description: backendErrorDescription(
                    e,
                    "De PDF kon niet worden gedownload. Probeer het later opnieuw."
                ),
                type: "error",
            });
        } finally {
            setIsDownloadingPdf(false);
        }
    }

    async function handleChangeReminderSettings(enabled: boolean, intervalDays: number) {
        if (!invoice) return;
        setIsSavingReminderSettings(true);
        try {
            await api.Me__business__changeSalesInvoiceReminderSettings({
                invoice_id: invoice.id,
                auto_reminder_enabled: enabled,
                reminder_interval_days: intervalDays,
            });
            dispatch({
                title: "Herinneringen bijgewerkt",
                description: enabled
                    ? "Automatische herinneringen staan aan totdat de factuur is betaald."
                    : "Automatische herinneringen staan uit.",
                type: "success",
            });
            await load({ showSpinner: false });
        } catch (e) {
            console.error(e);
            dispatch({
                title: "Fout opgetreden",
                description: backendErrorDescription(
                    e,
                    "De herinneringsinstellingen konden niet worden opgeslagen."
                ),
                type: "error",
            });
        } finally {
            setIsSavingReminderSettings(false);
        }
    }

    const backLink = (
        <button
            type="button"
            onClick={() =>
                navigate(isDraft ? "/invoices/drafts" : "/outstanding-sales-invoices")
            }
            className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
            <ChevronLeftIcon className="h-4 w-4" />
            {isDraft ? "Terug naar concepten" : "Terug naar facturen"}
        </button>
    );

    return (
        <Shell
            currentUser={currentUser}
            title={
                <div>
                    {backLink}
                    <div className="mt-4 flex items-start justify-between gap-4">
                        <div>
                            <h1 className="text-4xl font-normal tracking-tight text-gray-900">
                                {invoice
                                    ? invoice.invoice_number ?? "Concept — nummer volgt bij versturen"
                                    : "Factuur"}
                            </h1>
                            {invoice && (
                                <p className="mt-2 text-sm text-gray-600">
                                    {invoice.customer_name} ·{" "}
                                    {formatDateDDMMYYYY(invoice.invoice_date)}
                                </p>
                            )}
                        </div>
                        {displayStatus && <SalesInvoiceStatusPill status={displayStatus} />}
                    </div>
                </div>
            }
        >
            {isLoading && (
                <div className="grid gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2 h-[480px] animate-pulse rounded-xl bg-gray-100" />
                    <div className="flex flex-col gap-6">
                        <div className="h-64 animate-pulse rounded-xl bg-gray-100" />
                        <div className="h-40 animate-pulse rounded-xl bg-gray-100" />
                    </div>
                </div>
            )}
            {!isLoading && loadError && (
                <div className="p-8 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col items-start gap-4">
                    <p className="text-sm text-gray-700">{loadError}</p>
                    <SecondaryButton
                        title="Terug naar facturen"
                        onClick={() => navigate("/outstanding-sales-invoices")}
                    />
                </div>
            )}
            {!isLoading && !loadError && invoice && displayStatus && (
                <div className="grid items-start gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2 flex flex-col gap-6">
                        {invoice.is_credit_note && invoice.credited_invoice_number && (
                            <CreditNoteBanner
                                title={`Creditering van factuur ${invoice.credited_invoice_number}`}
                                description={invoice.credit_reason ?? undefined}
                            />
                        )}
                        <InvoiceDocumentCard
                            invoice={invoice}
                            displayStatus={displayStatus}
                            logoDataUri={logoDataUri}
                        />
                    </div>
                    <div className="flex flex-col gap-6">
                        <InvoiceActionsCard
                            displayStatus={displayStatus}
                            hasBeenSent={hasBeenSent}
                            isDownloadingPdf={isDownloadingPdf}
                            onSend={() =>
                                setEmailModalKind(!isDraft && hasBeenSent ? "resend" : "send")
                            }
                            onSendReminder={() => setEmailModalKind("reminder")}
                            onDownloadPdf={handleDownloadPdf}
                            onEditDraft={() => navigate(`/create-invoice?draft=${invoice.id}`)}
                            onDiscardDraft={() => setIsDiscardDraftOpen(true)}
                            canCredit={
                                !isDraft && !invoice.is_credit_note && !invoice.is_fully_credited
                            }
                            canEditDraft={!invoice.is_credit_note}
                            onCredit={() => navigate(`/credit-invoice?invoice=${invoice.id}`)}
                        />
                        <LinkedCreditNotesCard
                            creditNotes={invoice.credit_notes ?? []}
                            onOpen={(creditNoteId) =>
                                navigate(`/invoices/${creditNoteId}`)
                            }
                        />
                        {isUnpaid && (
                            <ReminderSettingsCard
                                autoReminderEnabled={invoice.auto_reminder_enabled}
                                reminderIntervalDays={invoice.reminder_interval_days}
                                isSaving={isSavingReminderSettings}
                                onChange={handleChangeReminderSettings}
                            />
                        )}
                        {displayTimeline && displayTimeline.length > 0 && (
                            <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                                <InvoiceTimeline entries={displayTimeline} isLoading={false} />
                            </div>
                        )}
                        {timelineFailed && (
                            <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                                    Tijdlijn
                                </h3>
                                <p className="text-sm text-gray-500">
                                    De tijdlijn kon niet worden geladen.
                                </p>
                                <SecondaryButton
                                    className="mt-3"
                                    title="Opnieuw proberen"
                                    onClick={() => load({ showSpinner: false })}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
            <SendInvoiceEmailModal
                open={emailModalKind !== null}
                kind={emailModalKind ?? "send"}
                invoiceNumber={invoice?.invoice_number ?? null}
                replyToEmail={invoiceContactEmail}
                initialToEmail={invoice?.customer_email}
                initialCcEmail={invoice?.customer_cc_email}
                intro={introForModal}
                defaultBody={defaultBodyForModal}
                isSubmitting={isSendingEmail}
                onClose={() => setEmailModalKind(null)}
                onSubmit={handleSendEmail}
            />
            <Popup
                open={isDiscardDraftOpen}
                onClose={() => setIsDiscardDraftOpen(false)}
                onConfirm={handleDiscardDraft}
                title="Concept verwijderen"
                confirmButtonDisplayLabel="Verwijderen"
                confirmButtonDisabled={isDiscardingDraft}
                variant="danger"
            >
                <p className="text-sm text-gray-500">
                    Weet je zeker dat je de conceptfactuur voor{" "}
                    <span className="font-medium text-gray-900">
                        {invoice?.customer_name || "deze klant"}
                    </span>{" "}
                    wilt verwijderen? Dit kan niet worden teruggedraaid.
                </p>
            </Popup>
        </Shell>
    );
}
